import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

const MIN = 1;
const MAX = 5;
const clampScale = (s) => Math.min(MAX, Math.max(MIN, s));

export function Lightbox({ images, index, onClose, onIndex }) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const moved = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const pinch = useRef({ dist: 0, scale: 1 });

  const reset = useCallback(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
    pointers.current.clear();
    pinch.current = { dist: 0, scale: 1 };
    dragging.current = false;
  }, []);

  const go = useCallback(
    (delta) => {
      reset();
      onIndex((index + delta + images.length) % images.length);
    },
    [index, images.length, onIndex, reset]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, go]);

  const img = images[index];
  if (!img) return null;

  const stepZoom = (dir) => {
    setScale((s) => {
      const ns = clampScale(Math.round((s + dir * 0.6) * 100) / 100);
      if (ns === 1) setPan({ x: 0, y: 0 });
      return ns;
    });
  };

  const onWheel = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const qx = e.clientX - (rect.left + rect.width / 2);
    const qy = e.clientY - (rect.top + rect.height / 2);
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    const ns = clampScale(scale * factor);
    if (ns === scale) return;
    if (ns === 1) {
      setScale(1);
      setPan({ x: 0, y: 0 });
      return;
    }
    setPan((p) => ({
      x: qx - ((qx - p.x) / scale) * ns,
      y: qy - ((qy - p.y) / scale) * ns,
    }));
    setScale(ns);
  };

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moved.current = false;
    if (pointers.current.size === 1) {
      last.current = { x: e.clientX, y: e.clientY };
      dragging.current = scale > 1;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } else if (pointers.current.size === 2) {
      dragging.current = false;
      const pts = [...pointers.current.values()];
      pinch.current.dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinch.current.scale = scale;
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (pinch.current.dist > 0) {
        const ns = clampScale((pinch.current.scale * dist) / pinch.current.dist);
        moved.current = true;
        setScale(ns);
        if (ns === 1) setPan({ x: 0, y: 0 });
      }
      return;
    }

    if (!dragging.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved.current = true;
    setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
    last.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current.dist = 0;
    if (pointers.current.size === 0) {
      dragging.current = false;
      if (!moved.current) {
        setScale((s) => {
          if (s > 1) {
            setPan({ x: 0, y: 0 });
            return 1;
          }
          return 2.4;
        });
      }
    }
  };

  const zoomed = scale > 1;

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-5 md:px-8 py-4 shrink-0" onClick={(e) => e.stopPropagation()}>
        <span className="text-[11px] uppercase tracking-[0.25em] text-white/60">
          {index + 1} / {images.length}
        </span>
        <div className="flex items-center gap-3">
          <button
            data-testid="lightbox-zoom-out"
            aria-label="Riduci"
            onClick={() => stepZoom(-1)}
            disabled={scale <= MIN}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-white/25 text-white/80 hover:bg-white/10 transition-colors disabled:opacity-30"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-[11px] tabular-nums text-white/60 w-10 text-center">{Math.round(scale * 100)}%</span>
          <button
            data-testid="lightbox-zoom-in"
            aria-label="Ingrandisci"
            onClick={() => stepZoom(1)}
            disabled={scale >= MAX}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-white/25 text-white/80 hover:bg-white/10 transition-colors disabled:opacity-30"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          <button data-testid="lightbox-close" aria-label="Chiudi" onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full border border-white/25 text-white/80 hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden px-4" onClick={(e) => e.stopPropagation()} onWheel={onWheel}>
        {images.length > 1 && (
          <button data-testid="lightbox-prev" aria-label="Precedente" onClick={() => go(-1)} className="absolute left-2 md:left-6 z-10 w-11 h-11 flex items-center justify-center rounded-full border border-white/25 text-white/80 hover:bg-white/10 transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        <img
          src={img.src}
          alt={img.label || "Immagine"}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          draggable={false}
          className={`max-w-full max-h-full object-contain ${dragging.current ? "" : "transition-transform duration-150"}`}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            cursor: zoomed ? "grab" : "zoom-in",
            touchAction: "none",
          }}
        />
        {images.length > 1 && (
          <button data-testid="lightbox-next" aria-label="Successiva" onClick={() => go(1)} className="absolute right-2 md:right-6 z-10 w-11 h-11 flex items-center justify-center rounded-full border border-white/25 text-white/80 hover:bg-white/10 transition-colors">
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {(img.label || img.caption) && (
        <div className="px-5 md:px-8 py-5 shrink-0 text-center" onClick={(e) => e.stopPropagation()}>
          {img.label && <p className="text-[11px] uppercase tracking-[0.25em] text-sage">{img.label}</p>}
          {img.caption && <p className="mt-1.5 text-sm text-white/60 max-w-2xl mx-auto leading-relaxed">{img.caption}</p>}
        </div>
      )}
    </div>,
    document.body
  );
}
